import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Authentication store.
 *
 * Access token is kept in memory only — never written to any storage.
 * The refresh token lives in an httpOnly cookie managed by the browser.
 *
 * Everything else (user identity, accessible workspaces, active workspace,
 * permissions, role) is persisted to **localStorage** so opening a new tab
 * or restoring a window doesn't bounce the user to the login screen.  On
 * boot, the app uses the persisted identity to render immediately while
 * silently exchanging the refresh-token cookie for a fresh access token.
 *
 * Every authenticated API request must include the X-Workspace-Id header
 * (injected automatically by the Axios client in api/client.js).
 */
export const useAuthStore = create(
  persist(
    (set, get) => ({
      /** @type {string|null} — memory only, never persisted */
      accessToken: null,

      /** @type {{id: string, email: string, fullName: string}|null} */
      user: null,

      /** @type {{id: string, name: string, slug: string, plan: string, role: string}|null} */
      workspace: null,

      /** @type {Array<{id: string, name: string, slug: string, plan: string, role: string}>} */
      workspaces: [],

      /** @type {string[]} — resolved permission list for current user+workspace */
      permissions: [],

      /** @type {string|null} — current user's role in the active workspace */
      role: null,

      /** @param {string} token */
      setAccessToken: (token) => set({ accessToken: token }),

      /**
       * Called after login or token refresh. Updates access token, user, and
       * workspaces list. Auto-selects the active workspace if there is only one,
       * or preserves the previously selected workspace if it is still in the list.
       *
       * @param {{access_token: string, user?: object, workspaces?: Array}} session
       */
      setSession: (session) =>
        set((prev) => {
          const workspaces = session?.workspaces ?? prev.workspaces ?? [];
          const currentWorkspaceId = prev.workspace?.id;
          let activeWorkspace = workspaces.find((o) => String(o.id) === String(currentWorkspaceId)) ?? null;
          if (!activeWorkspace && workspaces.length === 1) activeWorkspace = workspaces[0];
          return {
            accessToken: session?.access_token ?? null,
            user: session?.user ?? null,
            workspaces: workspaces,
            workspace: activeWorkspace,
            permissions: [],
            role: null,
          };
        }),

      /**
       * Set the active workspace (workspace switcher / workspace picker).
       * @param {{id: string, name: string, slug: string, plan: string, role: string}} workspace
       */
      setWorkspace: (workspace) => set({ workspace: workspace, permissions: [], role: null }),

      /**
       * Store the full list of accessible workspaces without changing the
       * active one. Used after invitation acceptance or other events that
       * mutate workspace membership without a full session re-issue.
       * @param {Array} workspaces
       */
      setWorkspaces: (workspaces) => set({ workspaces: workspaces }),

      /**
       * Store the resolved permission list and role returned by /api/v1/auth/me.
       * @param {string[]} permissions
       * @param {string} role
       */
      setPermissions: (permissions, role) => set({ permissions, role }),

      logout: () =>
        set({
          accessToken: null,
          user: null,
          workspace: null,
          workspaces: [],
          permissions: [],
          role: null,
        }),
    }),
    {
      name: "monitero-auth",
      storage: createJSONStorage(() => localStorage),
      // Access token must never be persisted — keep it in memory only.
      partialize: (state) => ({
        user: state.user,
        workspace: state.workspace,
        workspaces: state.workspaces,
        permissions: state.permissions,
        role: state.role,
        // accessToken excluded intentionally
      }),
    }
  )
);
